import { computed, ref, watch, Ref } from "vue";
import { useLookupStore } from "@/store";

export const useLookupFetcher = (
  name: string,
  baseUrl: string,
  config = {},
  options: { forceRefresh?: boolean; autoFetchEnabled?: boolean; clearOptionsOnEmptyInput?: boolean } = {
    autoFetchEnabled: true,
    clearOptionsOnEmptyInput: false
  },
  dependenciesRef?: Readonly<{ [key: string]: any }>,
  inputParams?: { [key: string]: any },
  searchParam?: string,
  searchText?: Ref<string>
) => {
  const store = useLookupStore();
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMore = ref(true);

  const getPageSize = (): number => {
    if ("responseLimit" in config) return (config as any).responseLimit as number;
    return 20;
  };

  const params = computed(() => {
    const finalQueryParams: Record<string, any> = {};

    if (dependenciesRef?.value) {
      for (const [key, value] of Object.entries(dependenciesRef.value)) {
        const depValue = getFormattedValue(value);
        if (depValue !== undefined && depValue !== null && depValue !== "") {
          finalQueryParams[key] = depValue;
        }
      }
    }

    if (searchParam && searchText?.value?.trim()) {
      finalQueryParams[searchParam] = searchText.value.trim();
    }

    if (inputParams && Object.keys(inputParams).length > 0) {
      Object.keys(inputParams).forEach((key) => {
        finalQueryParams[key] = inputParams[key];
      });
    }

    if ("responseLimit" in config) {
      const limitValue = config.responseLimit;
      finalQueryParams["limit"] = limitValue;
    }

    return finalQueryParams;
  });

  const extractValue = (value: any): string => {
    return typeof value === "object" && value !== null && "value" in value ? value.value : value;
  };

  const getFormattedValue = (value: any): any => {
    return Array.isArray(value) ? value.map((item) => extractValue(item)).join(",") : extractValue(value);
  };

  const fetch = async () => {
    if (!baseUrl || (options.clearOptionsOnEmptyInput && !searchText?.value?.trim())) {
      store.lookupStore[name] = [];
      return;
    }

    // Reset pagination state on every fresh fetch (new search or dependency change).
    hasMore.value = true;
    delete store.lookupTotal[name];
    loading.value = true;

    await store?.loadLookup(
      name,
      baseUrl,
      {
        ...config,
        params: params.value,
        dependencies: dependenciesRef?.value,
        searchParam,
        searchText: searchText?.value
      },
      options.forceRefresh ?? false,
      !!searchParam
    );

    loading.value = false;
  };

  const loadMore = async () => {
    if (!baseUrl || loading.value || loadingMore.value || !hasMore.value) return;

    const currentCount = store.lookupStore[name]?.length || 0;
    const pageSize = getPageSize();
    const knownTotal = store.lookupTotal[name];

    // Skip the API call if we already know all items are loaded.
    if (knownTotal !== undefined && currentCount >= knownTotal) {
      hasMore.value = false;
      return;
    }

    loadingMore.value = true;
    try {
      const newOptions = await store.fetchPagedOptions(name, baseUrl, {
        ...config,
        params: { ...params.value, offset: currentCount, limit: pageSize }
      });

      if (newOptions.length > 0) {
        store.appendToLookup(name, newOptions);
      }

      const total = store.lookupTotal[name];
      const loadedCount = currentCount + newOptions.length;
      hasMore.value = total === undefined ? newOptions.length >= pageSize : loadedCount < total;
    } finally {
      loadingMore.value = false;
    }
  };

  if (options.autoFetchEnabled) {
    watch([params, searchText], fetch, { immediate: true, deep: true });
  }

  return {
    options: computed(() => store?.lookupStore[name] || []),
    loading,
    loadingMore,
    hasMore,
    fetch,
    loadMore,
    reload: fetch,
    searchText,
    params
  };
};
